/** Recover a native contact-form return when Shopify drops template query parameters. */
(() => {
  try {
    const current = new URL(window.location.href);
    if (current.searchParams.get('contact_posted') !== 'true') return;
    const key = 'product-inquiry:return';
    const pending = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!pending?.destination) return;
    const destination = new URL(pending.destination, current.origin);
    if (destination.origin !== current.origin || destination.pathname !== current.pathname ||
        !Number.isFinite(pending.createdAt) || Date.now() - pending.createdAt > 30 * 60 * 1000) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.removeItem(key);
    // Only a confirmed contact submission on the same product can trigger recovery.
    if (current.searchParams.get('view') !== destination.searchParams.get('view') ||
        current.searchParams.get('variant') !== destination.searchParams.get('variant')) {
      destination.searchParams.set('contact_posted', 'true');
      window.location.replace(destination.pathname + destination.search + destination.hash);
    }
  } catch {
    // Native forms remain usable when session storage is unavailable.
  }
})();

/** Reusable product inquiry. Purchasing is managed exclusively by the theme. */
class ProductInquiry extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.product = JSON.parse(this.querySelector('[data-inquiry-product]').textContent);
    this.variant = this.product.variant;
    this.scope = this.closest('.shopify-section') || this.parentElement;
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('.product-inquiry__open');
    this.status = this.querySelector('[data-inquiry-status]');
    this.form = this.dialog.querySelector('form');
    this.storageKey = `product-inquiry:${this.dataset.productId}:${this.id}`;
    this.openButton.hidden = false;
    this.openButton.addEventListener('click', () => this.open(), { signal });
    this.querySelector('.product-inquiry__close').addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog.addEventListener('close', () => this.openButton.focus(), { signal });
    this.scope.addEventListener('variant:selected', event => {
      if (!this.isOwnPicker(event)) return;
      this.pending = true;
      this.updateState();
    }, { signal });
    this.scope.addEventListener('variant:update', event => {
      if (!this.isOwnPicker(event)) return;
      if (String(event.detail?.data?.productId) !== String(this.dataset.productId)) return;
      this.variant = event.detail.resource;
      this.pending = false;
      this.updateState();
    }, { signal });
    this.querySelector('[data-inquiry-delivery]')?.addEventListener('change', event => {
      const fields = this.querySelector('[data-inquiry-delivery-fields]');
      fields.hidden = !event.target.checked;
      fields.disabled = !event.target.checked;
    }, { signal });
    this.form?.addEventListener('submit', event => {
      if (!this.snapshot?.variantId) {
        event.preventDefault();
        this.status.textContent = 'Bitte wählen Sie eine Konfiguration und öffnen Sie die Anfrage erneut.';
        this.dialog.close();
        return;
      }
      // Store product context only; customer contact details are never stored here.
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.snapshot));
        const destination = this.form.querySelector('[name="return_to"]')?.value;
        if (destination) sessionStorage.setItem('product-inquiry:return', JSON.stringify({ destination, createdAt: Date.now() }));
      } catch {}
    }, { signal });
    this.updateState();
    const result = this.querySelector('[data-inquiry-result]');
    if (result) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (saved?.productId === this.dataset.productId) {
          this.snapshot = saved;
          this.renderSnapshot(result.dataset.inquiryResult !== 'success');
        }
        if (result.dataset.inquiryResult === 'success') {
          sessionStorage.removeItem(this.storageKey);
          const cleanedUrl = new URL(window.location.href);
          cleanedUrl.searchParams.delete('contact_posted');
          window.history.replaceState(window.history.state, '', cleanedUrl.href);
        }
      } catch {}
      this.dialog.showModal();
      if (result.dataset.inquiryResult === 'success') {
        this.form.hidden = true;
        this.dialog.prepend(result);
        result.focus();
      }
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    if (this.dialog?.open) this.dialog.close();
  }
  isOwnPicker(event) {
    return event.target.closest?.('variant-picker')?.closest('.shopify-section') === this.scope;
  }
  updateState() {
    this.openButton.disabled = Boolean(this.pending || !this.variant?.id);
    this.status.textContent = this.pending ? 'Konfiguration wird aktualisiert …' : !this.variant?.id ? 'Bitte wählen Sie eine gültige Konfiguration.' : '';
  }
  open() {
    if (this.pending || !this.variant?.id) return;
    const productForm = [...this.scope.querySelectorAll('product-form-component')]
      .find(form => String(form.dataset.productId) === String(this.dataset.productId));
    const selectedId = productForm?.querySelector('[name="id"]')?.value;
    if (selectedId && String(selectedId) !== String(this.variant.id)) {
      this.status.textContent = 'Bitte warten Sie, bis die gewählte Konfiguration geladen wurde.';
      return;
    }
    const quantity = Number(productForm?.querySelector('[name="quantity"]')?.value || 1);
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      this.status.textContent = 'Bitte geben Sie eine gültige Menge ein.';
      return;
    }
    const url = new URL(this.product.url);
    url.search = '';
    url.hash = '';
    url.searchParams.set('variant', this.variant.id);
    const options = this.variant.options || [this.variant.option1, this.variant.option2, this.variant.option3].filter(value => value != null);
    this.snapshot = {
      productId: this.dataset.productId,
      title: this.product.title,
      variantId: String(this.variant.id),
      configuration: this.product.options.map((name, index) => `${name}: ${options[index] || ''}`).join('\n'),
      quantity,
      url: url.href,
    };
    this.renderSnapshot();
    // Return to this product and the requested variant, including after captcha.
    const returnTo = this.form?.querySelector('[name="return_to"]');
    if (returnTo) {
      const returnUrl = new URL(url.pathname, window.location.origin);
      const currentUrl = new URL(window.location.href);
      // Preserve the chosen template and draft theme, without product-specific rules.
      for (const key of ['view', 'preview_theme_id', 'oseid', 'preview_key']) {
        if (currentUrl.searchParams.has(key)) returnUrl.searchParams.set(key, currentUrl.searchParams.get(key));
      }
      if (!returnUrl.searchParams.has('view') && this.dataset.template) {
        returnUrl.searchParams.set('view', this.dataset.template);
      }
      returnUrl.searchParams.set('variant', this.snapshot.variantId);
      returnUrl.searchParams.set('contact_posted', 'true');
      returnUrl.hash = this.id;
      returnTo.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
    }
    this.querySelector('[data-inquiry-result="success"]')?.remove();
    if (this.form) this.form.hidden = false;
    this.dialog.showModal();
  }
  renderSnapshot(showVariantLink = true) {
    const summary = this.querySelector('[data-inquiry-summary]');
    summary.replaceChildren();
    for (const text of [this.snapshot.title, this.snapshot.configuration, `Menge: ${this.snapshot.quantity}`]) {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      paragraph.style.whiteSpace = 'pre-line';
      summary.append(paragraph);
    }
    if (showVariantLink) {
      const link = document.createElement('a');
      link.textContent = 'Gewählte Variante ansehen';
      link.href = this.snapshot.url;
      summary.append(link);
    }
    const hidden = this.querySelector('[data-inquiry-hidden]');
    if (!hidden) return;
    hidden.replaceChildren();
    for (const [name, value] of Object.entries({Produkt: this.snapshot.title, 'Varianten-ID': this.snapshot.variantId, Konfiguration: this.snapshot.configuration, Menge: this.snapshot.quantity, Produktlink: this.snapshot.url})) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `contact[${name}]`;
      input.value = value;
      hidden.append(input);
    }
  }
}
if (!customElements.get('product-inquiry')) customElements.define('product-inquiry', ProductInquiry);
